'use client'
import React from 'react'
import Image from 'next/image'

import { cn } from '@/utils/twMerge'
import { useParams } from 'next/navigation'
import { getPath } from '@/utils/getHubPath'
import { getCMMCLinkType } from '@/utils/getCMMCLinkType'
import { FRAMEWORKS_WITH_CUSTOM_URLS } from '@/constants/hubFrameworks'
import { get, orderBy, findIndex } from '@/utils/lodashReplacements'
import { JSXMapSerializer, PrismicLink, PrismicRichText, PrismicText } from '@prismicio/react'

import HubSpotForm from '../common/HubSpotForm'
import OptimizedPrismicRichText from '../common/OptimizedPrismicRichText'

const Ebook = React.lazy(() => import('../blog/Ebook'))
const Product = React.lazy(() => import('../blog/Product'))
const Faq = React.lazy(() => import('@/components/blog/Faq'))
const Cta = React.lazy(() => import('@/components/common/CtaCommon'))
const CustomCode = React.lazy(() => import('@/components/blog/CustomCode'))
const RecommendedReading = React.lazy(() => import('./RecommendedReading'))

type DataObject = Record<string, any>
type ItemObject = Record<string, any>
type ArticleContentType = {
  uid: string
  type: string
  resources: DataObject
  data: DataObject
  linkType?: string
}

// PageHeader owns the article's only <h1>; CMS heading1 in DetailsSection fields must not add another.
const detailsSectionComponents: JSXMapSerializer = {
  heading1: ({ children }) => <h2>{children}</h2>,
}

import { useLocale } from '@/context/LocaleContext'

const ArticleContent: React.FC<ArticleContentType> = ({ uid, type, resources, data, linkType }) => {
  const locale = useLocale()
  const params = useParams()
  const articlesData = React.useMemo(
    () => orderBy(get(resources, uid, []), 'data.article_position', 'asc') as DataObject[],
    [resources, uid],
  )
  const articleIndex = findIndex(articlesData, ['data.article_position', data.data.article_position])
  const renderhubDetailsData = data.data?.slices || data.data.body

  const renderSection = (section: ItemObject, index: number) => {
    const key = `section-${index}`
    switch (section.slice_type) {
      case 'DetailsSection':
        if (!section.primary?.section_header || !section.primary?.section_contents) {
          return (
            <div
              className="hub-content-body scroll-mt-40 md:scroll-mt-36"
              id={section.primary?.section_id}
              key={key}>
              <p>Loading...</p>
            </div>
          )
        }
        return (
          <div
            className="hub-content-body scroll-mt-40 md:scroll-mt-36"
            id={section.primary?.section_id}
            key={key}>
            <OptimizedPrismicRichText field={section.primary.section_header} components={detailsSectionComponents} />
            <OptimizedPrismicRichText field={section.primary.section_contents} components={detailsSectionComponents} />
          </div>
        )
      case 'RecommendedReading':
        return <RecommendedReading data={section.primary} key={key} />
      case 'BlogFaq':
        return <Faq data={section} key={key} type="hub" />
      case 'Cta':
        if (section.variation === 'blogCta') {
          return section.primary.cta_type === 'EBOOK' ? (
            <Ebook data={section.primary} key={key} />
          ) : (
            <Product data={section.primary} key={key} />
          )
        }
        return <Cta data={section} key={key} type="hub" />
      case 'CustomCode':
        return <CustomCode data={section.primary} key={key} />
      case 'HubspotForm':
        return (
          <section key={key} className="mt-14">
            <div className="mx-auto max-w-3xl rounded-md border p-8 shadow-light backdrop-blur-25">
              <div className="mb-4 text-left">
                <PrismicRichText
                  field={section.primary.newsletter_opt_in_form_title}
                  components={detailsSectionComponents}
                />
              </div>
              <HubSpotForm
                portalId={section.primary.hubspot_form_portal_id}
                formId={section.primary.hubspot_form_form_id}
                type="opt-in-form"
              />
            </div>
          </section>
        )
      default:
        return null
    }
  }

  return (
    <>
      <article>{renderhubDetailsData.map(renderSection)}</article>

      <div className="flex items-center justify-between pt-10">
        <div className="flex flex-1 justify-start">
          {articleIndex > 0 && (
            <PrismicLink
              href={
                FRAMEWORKS_WITH_CUSTOM_URLS.includes(linkType as string)
                  ? getCMMCLinkType(articlesData[articleIndex - 1].uid, linkType as string, locale)
                  : `${getPath(type, locale)}/${articlesData[articleIndex - 1].uid}`
              }
              className="flex items-center [&:hover>img]:-translate-x-1 [&:hover>img]:rotate-180 [&:hover>span]:text-primary-green-85">
              <Image
                src="/images/angle-right-green.svg"
                width={20}
                height={16}
                alt=""
                aria-hidden="true"
                className="rotate-180 duration-500"
              />
              <span className="pl-4 duration-500">
                <PrismicText field={articlesData[articleIndex - 1].data.title} />
              </span>
            </PrismicLink>
          )}
        </div>
        <div className="flex flex-1 justify-end">
          {articleIndex < articlesData.length - 1 && (
            <PrismicLink
              href={
                FRAMEWORKS_WITH_CUSTOM_URLS.includes(linkType as string)
                  ? getCMMCLinkType(articlesData[articleIndex + 1].uid, linkType as string, locale)
                  : `${getPath(type, locale)}/${articlesData[articleIndex + 1].uid}`
              }
              className="flex items-center [&:hover>img]:translate-x-1 [&:hover>span]:text-primary-green-85">
              <span className="pr-4 duration-500">
                <PrismicText field={articlesData[articleIndex + 1].data.title} />
              </span>
              <Image
                src="/images/angle-right-green.svg"
                width={20}
                height={16}
                alt=""
                aria-hidden="true"
                className="-mt-1 duration-500"
              />
            </PrismicLink>
          )}
        </div>
      </div>

      <ul className="grid grid-cols-2 items-center gap-3 pt-10 md:flex">
        {[
          {
            name: 'x',
            url: `https://x.com/intent/tweet?text=${encodeURIComponent(data.data.title[0].text)}&url=${encodeURIComponent(`https://secureframe.com${getPath(type, locale)}/${params.uid}`)}`,
            text: 'Post',
            ariaLabel: 'Post on X (opens in a new tab)',
          },
          {
            name: 'facebook',
            url: `https://www.facebook.com/sharer/sharer.php?u=https://secureframe.com${getPath(type, locale)}/${params.uid}`,
            text: 'Share',
          },
          {
            name: 'linkedin',
            url: `http://www.linkedin.com/shareArticle?mini=true&title=${data.data.title[0].text}&url=https://secureframe.com${getPath(type, locale)}/${params.uid}`,
            text: 'Share',
          },
          {
            name: 'email',
            url: `mailto:?subject=${data.data.title[0].text}&body=https://secureframe.com${getPath(type, locale)}/${params.uid}`,
            text: 'Send',
          },
        ].map(({ name, url, text, ariaLabel }) => (
          <li key={name}>
            <a
              href={url}
              className={cn(
                'flex items-center rounded bg-primary-green-85 px-4 py-2 duration-500 hover:scale-105',
                name as string,
              )}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={ariaLabel}>
              <Image
                src={`/images/media/${name}.svg`}
                width={20}
                height={20}
                alt={name}
                className="not-sr-only mr-3 h-5 w-5"
              />
              <span className="not-sr-only">{text}</span>
            </a>
          </li>
        ))}
      </ul>
    </>
  )
}

export default React.memo(ArticleContent)
